import React from 'react';
import { Shield } from 'lucide-react';

/**
 * TrustStrip — Compact provider header in conversation.
 *
 * Phase-1.x cleanup: the per-conversation rating + completed-booking
 * metrics were removed from this header. They were rendered as small
 * unlabeled numbers ("★ 5 (1)", "✓ 3") which read as cryptic stats inside
 * a chat window. Ratings and completion counts are still surfaced on
 * vendor profiles, browse cards, and other public discovery surfaces —
 * only the in-thread copy is suppressed here.
 *
 * Reads from the `vendor_trust` object returned by the hydration endpoint.
 * Does NOT fabricate metrics — omits fields that aren't available.
 */
const TrustStrip = ({ vendorTrust, onClose, onNavigateToVendor }) => {
  if (!vendorTrust) return null;

  const { name, photo, badges, city, state, primary_service } = vendorTrust;
  const locationStr = [city, state].filter(Boolean).join(', ');

  return (
    <div
      data-testid="trust-strip"
      className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-slate-100"
    >
      {/* Avatar */}
      <div
        className={`relative shrink-0 ${onNavigateToVendor ? 'cursor-pointer' : ''}`}
        onClick={onNavigateToVendor || undefined}
        data-testid="trust-strip-vendor-avatar"
      >
        {photo && photo.startsWith('http') ? (
          <img
            src={photo}
            alt={name}
            className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-100"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 items-center justify-center text-white text-sm font-bold ${photo && photo.startsWith('http') ? 'hidden' : 'flex'}`}>
          {name?.charAt(0) || 'P'}
        </div>
        {badges?.some(b => b.label?.toLowerCase().includes('verified') || b.label?.toLowerCase().includes('identity')) && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center ring-2 ring-white">
            <Shield className="w-2.5 h-2.5 text-white" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onNavigateToVendor || undefined}
            className={`text-sm font-semibold truncate ${onNavigateToVendor ? 'text-slate-900 hover:text-emerald-700 hover:underline cursor-pointer' : 'text-slate-900'}`}
            data-testid="trust-strip-vendor-link"
          >
            {name || 'Service Provider'}
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
          {primary_service && <span className="truncate">{primary_service}</span>}
          {primary_service && locationStr && <span className="text-slate-300">|</span>}
          {locationStr && <span className="truncate">{locationStr}</span>}
        </div>
      </div>

      {/* Close button */}
      <button
        data-testid="conversation-close-btn"
        onClick={onClose}
        className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors shrink-0 ml-1"
        aria-label="Close conversation"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default TrustStrip;
